import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InfoTip } from "@/components/ui/tooltip";
import {
  axisLegendHint,
  chartRightMargin,
  formatAxisMoney,
  inferChartAxes,
  type ChartSeriesInput,
} from "@/lib/chartSeriesAxes";
import { cn, formatPLN } from "@/lib/utils";

export type DailyPoint = {
  date: string;
  [key: string]: string | number | undefined;
};

function formatDayLabel(isoDate: string): string {
  if (!isoDate || isoDate.length < 10) return isoDate;
  return `${isoDate.slice(8, 10)}.${isoDate.slice(5, 7)}`;
}

function formatValue(v: number, format: "number" | "pln" = "number"): string {
  if (format === "pln") return formatPLN(v);
  return String(Math.round(v * 10) / 10);
}

export default function DailyTrendChart({
  title,
  subtitle,
  data,
  series,
  mode = "bar",
  emptyNote = "Brak danych dziennych w tym okresie.",
  hideForSingleDay = true,
  className,
}: {
  title: string;
  subtitle?: string;
  data: DailyPoint[];
  series: ChartSeriesInput[];
  mode?: "bar" | "line";
  emptyNote?: string;
  hideForSingleDay?: boolean;
  className?: string;
}) {
  if (hideForSingleDay && data.length <= 1) {
    return (
      <Card className={cn(className)}>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-1.5">
            <CardTitle className="text-base">{title}</CardTitle>
            <InfoTip tipId="daily_chart" />
          </div>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Wykres dzienny jest dostępny dla okien 7 dni i dłuższych. Wybierz dłuższy okres, żeby
            zobaczyć zmiany dzień po dniu.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!data.length) {
    return (
      <Card className={cn(className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{emptyNote}</p>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((d) => ({
    ...d,
    label: formatDayLabel(String(d.date)),
  }));
  const resolved = inferChartAxes(series, chartData);
  const axisHint = axisLegendHint(resolved);
  const combinedSubtitle = [subtitle, axisHint].filter(Boolean).join(" · ");
  const hasRight = resolved.some((s) => s.axisId === "right");
  const hasMoney = resolved.some((s) => s.axisId === "rightMoney");
  const Chart = mode === "line" ? LineChart : BarChart;

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-1.5">
          <CardTitle className="text-base">{title}</CardTitle>
          <InfoTip tipId="daily_chart" />
        </div>
        {combinedSubtitle && (
          <p className="text-xs text-muted-foreground">{combinedSubtitle}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="h-[220px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <Chart
              data={chartData}
              margin={{ top: 8, right: chartRightMargin(resolved), left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis
                yAxisId="left"
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                allowDecimals={false}
              />
              {hasRight && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  allowDecimals={false}
                  width={32}
                />
              )}
              {hasMoney && (
                <YAxis
                  yAxisId="rightMoney"
                  orientation="right"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={10}
                  tickFormatter={formatAxisMoney}
                  width={44}
                  offset={hasRight ? 36 : 0}
                />
              )}
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value: number, name: string) => {
                  const spec = resolved.find((s) => s.label === name || s.key === name);
                  return [
                    formatValue(Number(value) || 0, spec?.format || "number"),
                    spec?.label || name,
                  ];
                }}
                labelFormatter={(label) => `Dzień ${label}`}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {resolved.map((s) =>
                mode === "line" ? (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    yAxisId={s.axisId}
                    stroke={s.color || "hsl(var(--brand))"}
                    strokeWidth={2}
                    dot={false}
                  />
                ) : (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    name={s.label}
                    yAxisId={s.axisId}
                    fill={s.color || "hsl(var(--brand))"}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={28}
                  />
                ),
              )}
            </Chart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
